<script lang="ts">
  import * as H from "../settings/hints.ts";
  import * as A from "../settings/amounts.ts";
  import { figure } from "../lib/personCard.ts";
  import { moneyCards } from "../lib/moneyCards.svelte.ts";
  import { catalogKey, commonName, customSplitText, shownCents, formatBp, receiptKey, type ComputedItem, type Item, type ItemFields } from "@split/core";
  import CardPicker from "../components/CardPicker.svelte";
  import Swatch from "../components/Swatch.svelte";
  import ItemEditor from "../components/ItemEditor.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { formatCents, formatMicro, initials, receiptName, shortDate } from "../lib/format.ts";

  let query = $state("");
  let unpaidOnly = $state(false);
  // Everyone starts on the whole group; "Your items" narrows it to what you're on.
  let personFilter = $state<string | null>(null);
  let editing = $state<{ item: Item | null; preset: Partial<ItemFields> } | null>(null);

  // ---- how the ledger looks: remembered on this device
  type Density = "detailed" | "compact" | "receipts";
  type Sort = "newest" | "oldest" | "priciest" | "cheapest" | "name";
  function remembered<T extends string>(key: string, fallback: T): T {
    try {
      return (localStorage.getItem(key) as T | null) ?? fallback;
    } catch {
      return fallback;
    }
  }
  function remember(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* fine */
    }
  }
  let density = $state<Density>(remembered("ledger:density", "compact"));
  let sort = $state<Sort>(remembered("ledger:sort", "newest"));
  /** Receipts you opened while "Receipts only" is on, or closed in the other views. */
  let flipped = $state<Set<string>>(new Set());
  $effect(() => remember("ledger:density", density));
  $effect(() => remember("ledger:sort", sort));
  $effect(() => {
    void density;
    flipped = new Set();
  });
  const isOpen = (key: string) => (density === "receipts") === flipped.has(key);
  function flip(key: string) {
    const next = new Set(flipped);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    flipped = next;
  }


  const L = $derived(app.ledger);
  const S = $derived(app.viewState);
  const people = $derived(S.personOrder.map((id) => S.people[id]!).filter((p) => p && !p.archived));
  const ownerId = $derived(S.settings.ownerPersonId);
  const ownerName = $derived((ownerId && S.people[ownerId]?.name) || "the treasurer");
  const notes = $derived(S.catalogNotes ?? {});
  const noteFor = (c: ComputedItem) => notes[catalogKey(c.item)];
  const TRIP_CHECK = "Rows don't add up to the printed receipt total (see Receipts).";

  const visible = $derived.by(() => {
    const q = query.trim().toLowerCase();
    return L.items.filter((c) => {
      if (personFilter && c.shares[personFilter] === undefined && c.item.paidBy !== personFilter) return false;
      if (unpaidOnly) {
        const ids = personFilter ? [personFilter] : Object.keys(c.status).filter((p) => p !== ownerId);
        if (!ids.some((p) => c.status[p] && c.status[p] !== "paid") && c.item.ticked.length > 0) return false;
      }
      if (!q) return true;
      const kw = noteFor(c)?.keywords.join(" ") ?? "";
      return `${c.item.name} ${c.item.itemNo} ${c.item.store} ${c.item.note} ${kw}`.toLowerCase().includes(q);
    });
  });

  /** Rows grouped by receipt (its id; before ids, date + store + label), newest first. */
  const trips = $derived.by(() => {
    const map = new Map<string, ComputedItem[]>();
    for (const c of visible) {
      const k = receiptKey(c.item);
      const list = map.get(k);
      if (list) list.push(c);
      else map.set(k, [c]);
    }
    const order = new Map(L.trips.map((t, i) => [t.key, i]));
    const byItem: Record<Sort, (a: ComputedItem, b: ComputedItem) => number> = {
      newest: () => 0,
      oldest: () => 0,
      priciest: (a, b) => b.totalMicro - a.totalMicro,
      cheapest: (a, b) => a.totalMicro - b.totalMicro,
      name: (a, b) => a.item.name.localeCompare(b.item.name),
    };
    const groups = [...map.entries()].map(([key, rows]) => ({
      key,
      rows: sort === "newest" || sort === "oldest" ? rows : [...rows].sort(byItem[sort]),
      info: L.trips[order.get(key)!]!,
    }));
    const byGroup: Record<Sort, (a: (typeof groups)[number], b: (typeof groups)[number]) => number> = {
      newest: (a, b) => order.get(a.key)! - order.get(b.key)!,
      oldest: (a, b) => order.get(b.key)! - order.get(a.key)!,
      priciest: (a, b) => b.info.totalMicro - a.info.totalMicro,
      cheapest: (a, b) => a.info.totalMicro - b.info.totalMicro,
      name: (a, b) => order.get(a.key)! - order.get(b.key)!,
    };
    return groups.sort(byGroup[sort]);
  });

  /** Puts every item on a receipt on one card (each change has its own history entry). */
  async function setCard(key: string, card: string) {
    const rows = L.items.filter((c) => receiptKey(c.item) === key);
    let n = 0;
    for (const c of rows) {
      if ((c.item.paidWith ?? "") === card) continue;
      const err = await app.do_({ type: "item.update", itemId: c.item.id, patch: { paidWith: card || null } });
      if (err) return;
      n++;
    }
    if (n) app.toast(card ? `Put ${n} ${n === 1 ? "item" : "items"} on ${card}` : `Cleared the card on ${n} ${n === 1 ? "item" : "items"}`);
  }

  const owing = $derived(L.people.filter((p) => p.status === "owes"));
  const summary = $derived.by(() => {
    const ms = app.memberSession;
    if (ms) {
      const me = L.byPersonId[ms.personId];
      if (!me) return "";
      return `${A.MY_BALANCE_LABEL} ${figure(shownCents(me.balanceMicro))}`;
    }
    if (owing.length === 0) return A.TOTALS_SQUARE;
    const total = formatCents(owing.reduce((s, p) => s + shownCents(p.balanceMicro), 0));
    return `${A.TOTAL_OWED_LABEL} ${total}`;
  });

  async function toggle(c: ComputedItem, pid: string) {
    if (!app.canTick(pid, c.item.id)) return;
    const on = c.item.ticked.includes(pid);
    const name = S.people[pid]?.name ?? "";
    const err = await app.do_({ type: "share.set", itemId: c.item.id, personId: pid, ticked: !on });
    if (!err && on) {
      app.toast(`Unticked ${name} on ${c.item.name}`, "info", {
        label: "Undo",
        run: () => void app.do_({ type: "share.set", itemId: c.item.id, personId: pid, ticked: true }),
      });
    }
  }

  function statusLabel(c: ComputedItem, pid: string): string {
    const n = S.people[pid]?.name ?? "";
    const st = c.status[pid];
    const share = formatMicro(c.shares[pid] ?? 0);
    if (pid === app.meId) return `${n} (you): ${share}`;
    if (pid === ownerId) return `${n} (treasurer): ${share}`;
    if (st === "paid") return `${n}: ${share}, paid`;
    if (st === "partial") return `${n}: ${share}, ${formatMicro(c.remaining[pid] ?? 0)} left`;
    return `${n}: ${share}, not paid`;
  }

  const swatchStatus = (c: ComputedItem, pid: string) => (pid === ownerId ? "paid" : (c.status[pid] ?? "unpaid"));
  const rowChecks = (c: ComputedItem) => c.checks.filter((x) => x !== TRIP_CHECK);
  const cols = $derived(`minmax(220px, 1fr) repeat(${people.length}, 34px) 96px`);
  /** Big groups: one column per person stops working, so rows show who's ticked under the name. */
  const many = $derived(people.length > 12);

  // Several of the same thing on one receipt show as one row (×2) until you open it.
  let openRepeats = $state<Record<string, boolean>>({});
  function repeatRows(rows: ComputedItem[], key: string) {
    return rows.filter((r) => r.repeat?.key === key);
  }
  function whoGot(rows: ComputedItem[]): string {
    const names = new Map<string, number>();
    for (const r of rows) for (const pid of r.item.ticked) {
      const n = S.people[pid]?.name;
      if (n) names.set(n, (names.get(n) ?? 0) + 1);
    }
    return [...names].map(([n, k]) => (k > 1 ? `${n} ×${k}` : n)).join(", ") || "nobody ticked yet";
  }
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Ledger</h1>
      <p class="lede">{summary}</p>
    </div>
    {#if !app.memberSession}<button class="btn primary" onclick={() => (editing = { item: null, preset: {} })} disabled={app.readOnly}>Add item</button>{/if}
  </div>

  <div class="view-bar">
    <div class="seg" role="group" aria-label="View">
      <button aria-pressed={density === "detailed"} onclick={() => (density = "detailed")} title="Every detail on each item">Detailed</button>
      <button aria-pressed={density === "compact"} onclick={() => (density = "compact")} title="One line per item">Compact</button>
      <button aria-pressed={density === "receipts"} onclick={() => (density = "receipts")} title="Just the receipts; open one to see its items">Receipts only</button>
    </div>
    <label class="sort">
      <span class="muted">Sort</span>
      <select class="input" bind:value={sort} aria-label="Sort">
        <option value="newest">Newest first</option>
        <option value="oldest">Oldest first</option>
        <option value="priciest">Priciest first</option>
        <option value="cheapest">Cheapest first</option>
        <option value="name">Name A to Z</option>
      </select>
    </label>
  </div>

  <div class="filters">
    <input class="input search" type="search" placeholder="Search items, stores, item numbers, keywords" bind:value={query} aria-label="Search" />
    <label class="toggle">
      <input type="checkbox" bind:checked={unpaidOnly} />
      Unpaid only
    </label>
    {#if app.memberSession}
      {@const me = app.memberSession.personId}
      <div class="seg" role="group" aria-label="Which items">
        <button aria-pressed={personFilter === null} onclick={() => (personFilter = null)}>Everything</button>
        <button aria-pressed={personFilter === me} onclick={() => (personFilter = me)}>Your items</button>
      </div>
    {:else if many}
      <select class="input who-pick" aria-label="Show one person" value={personFilter ?? ""} onchange={(e) => (personFilter = (e.currentTarget as HTMLSelectElement).value || null)}>
        <option value="">Everyone ({people.length})</option>
        {#each [...people].sort((a, b) => a.name.localeCompare(b.name)) as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
      </select>
    {:else}
    <div class="who" role="group" aria-label="Show one person">
      {#each people as p (p.id)}
        <button
          class="who-btn"
          class:on={personFilter === p.id}
          aria-pressed={personFilter === p.id}
          onclick={() => (personFilter = personFilter === p.id ? null : p.id)}
          title={p.name}
        >
          <Swatch color={p.color} status={personFilter === p.id ? "paid" : "unpaid"} size={16} />
          <span>{p.name}</span>
        </button>
      {/each}
    </div>
    {/if}
  </div>

  {#if L.items.length === 0}
    <div class="list empty">
      <p>Nothing here yet. Add the first item from a receipt, or import your Google Sheet in Settings.</p>
    </div>
  {:else if trips.length === 0}
    <div class="list empty"><p>No items match. Clear the search or filters to see everything.</p></div>
  {:else}
    <div class="ledger" class:many style:--cols={cols}>
      <div class="colhead" aria-hidden="true">
        <span>Item</span>
        {#each people as p (p.id)}
          <span class="ph" title={p.name}><Swatch color={p.color} status="paid" size={26} text={initials(p.name)} /></span>
        {/each}
        <span class="r">Total</span>
      </div>

      {#each trips as t (t.key)}
        {@const off = !app.memberSession && t.info.diffMicro !== null && Math.abs(t.info.diffMicro) > 5000}
        {@const open = isOpen(t.key)}
        <section class="trip" class:closed={!open}>
          <header class="trip-head">
            <div>
              <button class="fold" aria-expanded={open} aria-label="{open ? 'Hide' : 'Show'} items on {receiptName(t.info.store, t.info.receipt)}" onclick={() => flip(t.key)}>
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M8 10l4 4 4-4" /></svg>
              </button>
              <h2>{receiptName(t.info.store, t.info.receipt)}</h2>
              <span class="muted">{shortDate(t.info.date)}, {t.info.itemCount} {t.info.itemCount === 1 ? "item" : "items"}</span>
              {#if app.canEdit}
                <span class="card-pick" title="Card for every item on this receipt">
                  <CardPicker
                    value={t.info.cards.length === 1 ? t.info.cards[0]! : ""}
                    cards={moneyCards.with(S.settings.cards)}
                    label="Card for this receipt"
                    placeholder={t.info.cards.length > 1 ? `${t.info.cards.length} cards` : "Card…"}
                    onchange={(v) => v && setCard(t.key, v)}
                  />
                </span>
                <button class="add-here" onclick={() => (editing = { item: null, preset: { date: t.info.date, store: t.info.store, receipt: t.info.receipt, receiptId: t.info.receiptId, paidWith: t.info.cards[0] ?? null } })}>Add item</button>
              {:else if t.info.cards.length}
                <span class="card-tag">{t.info.cards.join(", ")}</span>
              {/if}
              {#if off}
                <span class="off">Receipt says {formatMicro(t.info.receiptCents! * 10_000)}; rows add up to {formatMicro(Math.abs(t.info.diffMicro!))} {t.info.diffMicro! > 0 ? "more" : "less"}.</span>
              {/if}
            </div>
            {#if app.memberSession && personFilter}
              <strong class="num trip-total" title="Your part of this receipt">{formatMicro(t.rows.reduce((s, r) => s + (r.shares[app.memberSession!.personId] ?? 0), 0))}</strong>
            {:else}
              <strong class="num trip-total">{formatMicro(t.info.totalMicro)}</strong>
            {/if}
          </header>

          {#each open ? t.rows : [] as c (c.item.id)}
            {@const checks = rowChecks(c)}
            {@const note = noteFor(c)}
            {@const folded = c.repeat?.samePrice && !openRepeats[c.repeat.key]}
            {#if folded && c.repeat?.index === 1}
              {@const group = repeatRows(t.rows, c.repeat.key)}
              <button class="row repeat-row" onclick={() => (openRepeats[c.repeat!.key] = true)} title="Show each one to tick who got it">
                <span class="name">
                  <span class="title">{c.item.name}{#if commonName(c.item.name, note)}{" "}<span class="muted common">({commonName(c.item.name, note)})</span>{/if} <span class="times">×{group.length}</span></span>
                  <span class="sub">
                    {#if c.item.itemNo}<span class="code">#{c.item.itemNo}</span>{/if}
                    <span>{whoGot(group)}</span>
                  </span>
                </span>
                <span class="total num">{formatMicro(group.reduce((s, g) => s + g.totalMicro, 0))}<span class="each">{group.length} × {formatMicro(c.totalMicro)}</span></span>
              </button>
            {/if}
            {#if !folded}
            <div class="row" class:pending={app.pendingTouches.items.has(c.item.id)} class:detailed={density === "detailed"}>
              <button class="name" onclick={() => (editing = { item: c.item, preset: {} })}>
                <span class="title">{c.item.name}{#if commonName(c.item.name, note)}{" "}<span class="muted common">({commonName(c.item.name, note)})</span>{/if}</span>
                <span class="sub">
                  {#if c.item.itemNo}<span class="code">#{c.item.itemNo}</span>{/if}
                  {#if c.repeat}
                    <span class="tag">{c.repeat.samePrice ? `${c.repeat.index} of ${c.repeat.count}` : `package ${c.repeat.index} of ${c.repeat.count} · priced by weight`}</span>
                  {/if}
                  {#if customSplitText(S, c.item)}<span class="tag">{customSplitText(S, c.item)}</span>{/if}
                  {#if c.item.paidWith && t.info.cards.length > 1}<span class="tag">{c.item.paidWith}</span>{/if}
                  {#if c.item.paidBy}<span class="tag">{S.people[c.item.paidBy]?.name} paid the store</span>{/if}
                  {#if checks.length}<span class="warn" title={checks.join("\n")}>{checks[0]}</span>{/if}
                </span>
                {#if density === "detailed"}
                  <span class="detail-line">
                    <span class="num">{formatMicro(c.item.priceCents * 10_000)}</span>
                    {#if c.taxBp}<span>+ {formatBp(c.taxBp)} tax</span>{/if}
                    {#if c.tipBp}<span>+ {formatBp(c.tipBp)} tip</span>{/if}
                    {#if c.item.couponCents}<span>− {formatMicro(-c.item.couponCents * 10_000)} coupon</span>{/if}
                    {#if c.item.crvCents}<span>+ {formatMicro(c.item.crvCents * 10_000)} CRV</span>{/if}
                    <span>{c.category}</span>
                    {#if c.item.paidWith && t.info.cards.length > 1}<span>{c.item.paidWith}</span>{/if}
                    {#if note?.keywords.length}<span class="kw">{note.keywords.join(", ")}</span>{/if}
                    {#if note?.taxable === false}<span class="kw">usually not taxed</span>{/if}
                    {#if c.item.note}<span class="kw">"{c.item.note}"</span>{/if}
                  </span>
                  {#if c.item.ticked.length > 1}
                    <span class="detail-line">
                      {#each c.item.ticked as pid (pid)}
                        {#if S.people[pid]}<span>{S.people[pid]!.name} {formatMicro(c.shares[pid] ?? 0)}</span>{/if}
                      {/each}
                    </span>
                  {/if}
                {/if}
              </button>

              <!-- wide screens: one column per person, like the Sheet -->
              {#each people as p (p.id)}
                {@const on = c.item.ticked.includes(p.id)}
                <span class="cell">
                  <button
                    class="tick"
                    class:on
                    onclick={() => toggle(c, p.id)}
                    disabled={!app.canTick(p.id, c.item.id)}
                    aria-pressed={on}
                    aria-label={on ? `${statusLabel(c, p.id)}. Untick` : `Tick ${p.name} on ${c.item.name}`}
                    title={on ? statusLabel(c, p.id) : `Tick ${p.name}`}
                  >
                    <Swatch color={p.color} status={on ? swatchStatus(c, p.id) : "off"} size={20} />
                  </button>
                </span>
              {/each}

              <!-- phones: just the people ticked -->
              <span class="mini">
                {#each c.item.ticked as pid (pid)}
                  {@const p = S.people[pid]}
                  {#if p}<Swatch color={p.color} status={swatchStatus(c, pid)} size={18} label={statusLabel(c, pid)} />{/if}
                {:else}
                  {#if H.LEDGER_NOBODY_TICKED.trim()}<span class="muted small">{H.LEDGER_NOBODY_TICKED}</span>{/if}
                {/each}
                {#if app.memberSession}
                  {@const me = app.memberSession.personId}
                  {@const mine = c.item.ticked.includes(me)}
                  {@const open = app.canTick(me, c.item.id)}
                  {#if open || mine}
                    <button class="mine" class:on={mine} aria-pressed={mine} disabled={!open} onclick={() => toggle(c, me)} aria-label="{c.item.name} was mine">
                      {mine ? "Mine" : "Was this yours?"}
                    </button>
                  {/if}
                {/if}
              </span>

              <span class="total num">
                {#if app.memberSession && c.shares[app.memberSession.personId] !== undefined && c.item.ticked.length > 1}
                  {formatMicro(c.shares[app.memberSession.personId]!)}
                  <span class="each">your part of {formatMicro(c.totalMicro)}</span>
                {:else}
                  {formatMicro(c.totalMicro)}
                  {#if c.item.ticked.length > 1}<span class="each">{c.item.ticked.length} ways</span>{/if}
                {/if}
              </span>
            </div>
            {#if c.repeat?.samePrice && c.repeat.index === c.repeat.count}
              <button class="link-btn fold-again" onclick={() => (openRepeats[c.repeat!.key] = false)}>Show as one row (×{c.repeat.count})</button>
            {/if}
            {/if}
          {/each}
          {#if !open}
            <button class="peek" onclick={() => flip(t.key)}>Show {t.rows.length} {t.rows.length === 1 ? "item" : "items"}</button>
          {/if}
        </section>
      {/each}
    </div>
    <p class="key muted">
      <Swatch color="Blue" status="paid" size={14} /> paid
      <Swatch color="Blue" status="partial" size={14} /> partly paid
      <Swatch color="Blue" status="unpaid" size={14} /> not paid
      <Swatch color="Blue" status="off" size={14} /> not ticked
    </p>
  {/if}
</div>

{#if editing}
  <ItemEditor item={editing.item} preset={editing.preset} onclose={() => (editing = null)} />
{/if}

<style>
  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;
    align-items: center;
    margin-bottom: 18px;
  }

  .search {
    flex: 1 1 260px;
    max-width: 380px;
  }

  .toggle {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    font-size: var(--t-sm);
    font-weight: 550;
  }

  .toggle input {
    width: 18px;
    height: 18px;
    accent-color: var(--ink);
  }

  .who {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .who-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 6px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    font-size: var(--t-xs);
    font-weight: 550;
    color: var(--ink-2);
    cursor: pointer;
  }

  .who-btn:hover {
    background: var(--wash);
  }

  .who-btn.on {
    background: var(--surface);
    border-color: var(--rule-strong);
    color: var(--ink);
  }

  .ledger {
    background: var(--surface);
    border: 1px solid var(--rule);
    border-radius: var(--r-lg);
    overflow: clip;
  }

  .colhead,
  .row {
    display: grid;
    grid-template-columns: var(--cols);
    align-items: center;
    column-gap: 4px;
    padding: 0 14px;
  }

  .colhead {
    position: sticky;
    top: 0;
    z-index: 5;
    min-height: 46px;
    background: var(--surface);
    border-bottom: 1px solid var(--rule-strong);
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--muted);
  }

  .ph {
    display: grid;
    place-items: center;
  }

  .r {
    text-align: right;
  }

  .trip + .trip {
    border-top: 6px solid var(--paper);
  }

  .trip-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 14px 8px;
    background: var(--surface);
  }

  .trip-head h2 {
    font-size: var(--t-lg);
    display: inline;
    margin-right: 8px;
  }

  .trip-head .muted {
    font-size: var(--t-sm);
  }

  .off {
    display: block;
    margin-top: 4px;
    color: var(--owes);
    font-size: var(--t-sm);
    font-weight: 550;
  }

  .trip-total {
    font-size: var(--t-lg);
  }

  .add-here {
    margin-left: 10px;
    padding: 2px 4px;
    border: 0;
    background: none;
    color: var(--focus);
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .add-here:hover {
    text-decoration: underline;
  }

  .view-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
    margin-bottom: 12px;
  }

  .seg {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: var(--wash);
  }

  .seg button {
    padding: 6px 12px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .sort {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: var(--t-sm);
  }

  .sort select {
    width: auto;
  }

  .fold {
    display: inline-grid;
    place-items: center;
    width: 24px;
    height: 24px;
    margin-right: 4px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: none;
    color: var(--muted);
    cursor: pointer;
    vertical-align: middle;
  }

  .fold:hover {
    background: var(--wash);
  }

  .fold path {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .trip.closed .fold svg {
    transform: rotate(-90deg);
  }

  .peek {
    display: block;
    width: 100%;
    padding: 8px 14px;
    border: 0;
    border-top: 1px solid var(--rule);
    background: none;
    color: var(--focus, #3b7ddd);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }


  .detail-line {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .kw {
    font-style: italic;
  }

  .card-pick {
    display: inline-block;
    width: 170px;
    margin-left: 10px;
    vertical-align: middle;
  }

  .card-pick :global(.trigger) {
    min-height: 26px;
    padding: 2px 10px;
    border-radius: 999px;
    background: var(--wash);
    color: var(--ink-2);
    font-size: var(--t-xs);
  }

  .card-tag {
    margin-left: 10px;
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .row {
    min-height: 52px;
    border-top: 1px solid var(--rule);
  }

  .row:hover {
    background: color-mix(in srgb, var(--wash) 60%, transparent);
  }

  .row.pending .name .title::after {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 8px;
    border-radius: 50%;
    border: 1.5px solid var(--focus);
    vertical-align: middle;
  }

  .name {
    display: grid;
    gap: 1px;
    padding: 8px 0;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
    min-width: 0;
  }

  .title .common {
    font-weight: 400;
  }
  .title {
    font-weight: 560;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sub {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .tag {
    color: var(--ink-2);
  }

  .warn {
    color: #a86b00;
  }

  .cell {
    display: grid;
    place-items: center;
  }

  .tick {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: none;
    cursor: pointer;
    padding: 0;
  }

  .tick:not(.on) :global(.swatch) {
    opacity: 0;
  }

  .row:hover .tick:not(.on) :global(.swatch),
  .tick:focus-visible :global(.swatch) {
    opacity: 1;
  }

  .tick:disabled {
    cursor: default;
  }

  .mini {
    display: none;
  }

  .total {
    text-align: right;
    font-weight: 560;
    display: grid;
    line-height: 1.15;
  }

  .each {
    font-size: var(--t-xs);
    color: var(--muted);
    font-weight: 450;
  }

  .key {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    align-items: center;
    margin-top: 12px;
    font-size: var(--t-xs);
  }

  .small {
    font-size: var(--t-xs);
  }

  /* Big groups on any screen: the same layout as phones (ticked people under the name). */
  .ledger.many .colhead {
    display: none;
  }

  .ledger.many .row {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "name total"
      "mini total";
    padding: 8px 14px;
    row-gap: 4px;
  }

  .ledger.many .name {
    grid-area: name;
    padding: 0;
  }

  .ledger.many .cell {
    display: none;
  }

  .ledger.many .mini {
    grid-area: mini;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
  }

  .mine {
    margin-left: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    border: 1px solid var(--rule-strong);
    background: var(--surface);
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-xs);
    cursor: pointer;
  }

  .mine.on {
    background: var(--ink);
    border-color: var(--ink);
    color: var(--surface);
  }

  .mine:disabled {
    cursor: default;
    opacity: 0.7;
  }

  .ledger.many .total {
    grid-area: total;
  }

  .who-pick {
    width: auto;
    max-width: 260px;
  }

  /* Phones and narrow windows: drop the person columns, show ticked swatches under the name. */
  @media (max-width: 1000px) {
    .colhead {
      display: none;
    }

    .row {
      grid-template-columns: 1fr auto;
      grid-template-areas:
        "name total"
        "mini total";
      padding: 8px 14px;
      row-gap: 4px;
    }

    .name {
      grid-area: name;
      padding: 0;
    }

    .cell {
      display: none;
    }

    .mini {
      grid-area: mini;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }

    .total {
      grid-area: total;
    }

    .who-btn span {
      display: none;
    }

    .who-btn {
      padding: 4px;
    }
  }

  .row.repeat-row {
    width: 100%;
    background: var(--surface);
    border: 0;
    border-top: 1px solid var(--rule);
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
    grid-template-columns: 1fr auto;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .repeat-row .name {
    display: grid;
    gap: 2px;
  }

  .times {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-xs);
    font-weight: 700;
  }

  .fold-again {
    margin: 2px 16px 6px;
    font-size: var(--t-xs);
  }
</style>
